// EventEmitter tipado — única ponte React ↔ Phaser.
// React e Phaser importam a mesma instância singleton.
// Eventos em STICKY_EVENTS são "sticky": o último valor é cacheado
// e entregue a listeners que se registram depois do emit (resolve
// race entre montagem do React e cenas do Phaser).

import type { GameBusEvents } from '@shared/types'

type EventKey = keyof GameBusEvents
type Handler<K extends EventKey> = (payload: GameBusEvents[K]) => void

const STICKY_EVENTS: ReadonlySet<EventKey> = new Set<EventKey>(['stage:start'])

class TypedBus {
  private listeners = new Map<EventKey, Set<Handler<EventKey>>>()
  private sticky = new Map<EventKey, unknown>()

  on<K extends EventKey>(event: K, handler: Handler<K>): () => void {
    let set = this.listeners.get(event)
    if (!set) {
      set = new Set()
      this.listeners.set(event, set)
    }
    set.add(handler as Handler<EventKey>)
    if (STICKY_EVENTS.has(event) && this.sticky.has(event)) {
      ;(handler as Handler<K>)(this.sticky.get(event) as GameBusEvents[K])
    }
    return () => this.off(event, handler)
  }

  off<K extends EventKey>(event: K, handler: Handler<K>): void {
    this.listeners.get(event)?.delete(handler as Handler<EventKey>)
  }

  emit<K extends EventKey>(event: K, payload: GameBusEvents[K]): void {
    if (STICKY_EVENTS.has(event)) this.sticky.set(event, payload)
    const set = this.listeners.get(event)
    if (!set) return
    for (const h of set) {
      ;(h as Handler<K>)(payload)
    }
  }

  clearSticky(event?: EventKey): void {
    if (event) this.sticky.delete(event)
    else this.sticky.clear()
  }

  clear(): void {
    this.listeners.clear()
    this.sticky.clear()
  }
}

export const GameBus = new TypedBus()
